import { detectPitch, rmsLevel } from './yin';

export const SAMPLE_RATE = 44100;
const BUFFER_SIZE = 2048;
const SILENCE_THRESHOLD = 0.01;

export interface AudioFrame {
  frequency: number;
  rms: number;
  buffer: Float32Array;
}

type FrameCallback = (frame: AudioFrame) => void;

export class AudioCapture {
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;
  private onFrame: FrameCallback;

  constructor(onFrame: FrameCallback) {
    this.onFrame = onFrame;
  }

  async start(): Promise<void> {
    this.context = new AudioContext({ sampleRate: SAMPLE_RATE });

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        sampleRate: SAMPLE_RATE,
        channelCount: 1,
      },
    });

    this.source = this.context.createMediaStreamSource(this.stream);

    // ScriptProcessorNode for broad browser support (AudioWorklet optional upgrade)
    this.processor = this.context.createScriptProcessor(BUFFER_SIZE, 1, 1);

    this.processor.onaudioprocess = (e) => {
      const inputBuffer = e.inputBuffer.getChannelData(0);
      const buffer = new Float32Array(inputBuffer);
      const rms = rmsLevel(buffer);

      const frequency = rms > SILENCE_THRESHOLD
        ? detectPitch(buffer, this.context!.sampleRate)
        : -1;

      this.onFrame({ frequency, rms, buffer });
    };

    this.source.connect(this.processor);
    this.processor.connect(this.context.destination);
  }

  stop(): void {
    this.processor?.disconnect();
    this.source?.disconnect();
    this.stream?.getTracks().forEach(t => t.stop());
    this.context?.close();
    this.processor = null;
    this.source = null;
    this.stream = null;
    this.context = null;
  }

  get isRunning(): boolean {
    return this.context !== null;
  }
}
